import { useState, useEffect, useRef } from 'react';
import { cn } from '@/lib/utils';

export function useCountUp(
  end: number,
  active: boolean,
  duration = 1500
): number {
  const [count, setCount] = useState(0);
  const hasStarted = useRef(false);

  useEffect(() => {
    if (!active || hasStarted.current) return;
    if (end <= 0) {
      setCount(0);
      return;
    }

    hasStarted.current = true;
    const startTime = performance.now();
    let rafId: number;

    const animate = () => {
      const elapsed = performance.now() - startTime;
      const progress = Math.min(elapsed / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 4);
      setCount(Math.round(eased * end));
      if (progress < 1) {
        rafId = requestAnimationFrame(animate);
      }
    };

    rafId = requestAnimationFrame(animate);
    return () => cancelAnimationFrame(rafId);
  }, [active, end, duration]);

  return count;
}

export function Counter({
  value,
  active,
  duration = 1500,
  suffix = '',
  className,
}: Readonly<{
  value: number;
  active: boolean;
  duration?: number;
  suffix?: string;
  className?: string;
}>) {
  const count = useCountUp(value, active, duration);
  return (
    <span className={cn('tabular-nums', className)}>
      {count.toLocaleString('th-TH')}
      {suffix}
    </span>
  );
}
